import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { InView } from 'react-intersection-observer'
import { Dropdown, DropdownContent, DropdownItem, SpinnerLoader } from 'shared/ui'
import {
  type ITextFieldProps,
  makeTextFieldIcon,
  TextField,
  TextFieldPlaceholder,
} from 'shared/ui/TextField'

import styles from './styles.module.scss'

export type IEnrollSearchStore<T> = {
  loading: boolean
  hasMore: boolean
  termItems: T[]
  termItemsEmpty: boolean
  initialLoading: boolean
  loadMore: () => void
  searchItems: (term: string) => void
  reset: () => void
}

type IChatbotEnrollModalSearchProps<T extends { id: number; name: string }> = {
  onSelect: (item: T) => void
  textFieldProps?: ITextFieldProps
  selectedItem?: T | null
  renderDropdownFooter?: (closeDropdown: () => void) => ReactNode
  searchStore: IEnrollSearchStore<T>
}

function ChatbotEnrollModalSearchFn<T extends { id: number; name: string }>({
  onSelect,
  textFieldProps,
  selectedItem,
  renderDropdownFooter,
  searchStore: store,
}: IChatbotEnrollModalSearchProps<T>) {
  const [term, setTerm] = useState('')
  const ref = useRef<HTMLLabelElement | null>(null)

  const [isActive, setActive] = useState(false)
  const [selectionLocal, setSelectionLocal] = useState<T | null>(null)

  const selection = selectedItem === undefined ? selectionLocal : selectedItem

  const handleLoadMore = useCallback(() => {
    const loadMore = isActive && !store.loading && store.hasMore

    if (loadMore) store.loadMore()
  }, [isActive, store.loading, store.hasMore])

  const handleSelect = useCallback(
    (item: T) => {
      setActive(false)
      setTerm('')
      setSelectionLocal(item)
      onSelect(item)
    },
    [onSelect]
  )

  const isSearchLoading = store.termItemsEmpty && store.loading
  const isSearchHasMore = !store.termItemsEmpty && store.hasMore
  const isSearchNoResults = store.termItemsEmpty && !store.loading

  useEffect(() => {
    if (isActive) {
      store.loadMore()
    } else {
      store.reset()
      setTerm('')
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive) return
    store.searchItems(term)
  }, [term])

  return (
    <Dropdown
      show={isActive}
      onChangeOpen={setActive}
      placement='bottom-start'
      triggerComponent={() => (
        <TextFieldPlaceholder
          ref={ref}
          variant='stroke'
          size='medium'
          rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
          placeholder={
            selection ? (
              <div className={styles.dropdownSelection}>{selection.name}</div>
            ) : (
              <div className={styles.dropdownPlaceholder}>Select</div>
            )
          }
          {...textFieldProps}
        />
      )}
      customComponent={() => (
        <DropdownContent
          noPadding
          width={ref.current?.offsetWidth}
          className={styles.dropdownContent}
        >
          <TextField
            size='medium'
            variant='integrated'
            InputProps={{ placeholder: 'Select' }}
            value={term}
            onChange={setTerm}
          />
          <div className={styles.dropdownList}>
            {store.initialLoading ? (
              isActive && (
                <SpinnerLoader
                  ariaLabel={'ChatbotEnrollModalSearch'}
                  className={styles.dropdownSpinner}
                />
              )
            ) : (
              <>
                {store.termItems.map((item) => (
                  <DropdownItem
                    key={item.id}
                    item={{ id: item.id, label: item.name }}
                    onChange={() => handleSelect(item)}
                  />
                ))}
                {isSearchLoading && (
                  <SpinnerLoader
                    ariaLabel={'ChatbotEnrollModalSearch'}
                    className={styles.dropdownSpinner}
                  />
                )}
                <InView onChange={(inView) => inView && handleLoadMore()}>
                  {({ ref: inViewRef }) => (
                    <div ref={inViewRef}>
                      {isSearchHasMore && (
                        <SpinnerLoader
                          ariaLabel={'ChatbotEnrollModalSearch'}
                          className={styles.dropdownSpinner}
                        />
                      )}
                      {isSearchNoResults && (
                        <div className={styles.dropdownNotFound}>No results found</div>
                      )}
                    </div>
                  )}
                </InView>
              </>
            )}
          </div>
          {renderDropdownFooter && (
            <div className={styles.dropdownFooter}>
              {renderDropdownFooter(() => setActive(false))}
            </div>
          )}
        </DropdownContent>
      )}
    />
  )
}

export const ChatbotEnrollModalSearch = observer(
  ChatbotEnrollModalSearchFn
) as typeof ChatbotEnrollModalSearchFn
